import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Box } from "@mui/material";
import ResponsiveContentWrapper from "../layout/ResponsiveContentWrapper";
import ScreenLoader from "../Common/ScreenLoader/ScreenLoader";
import EmptyState from "../Common/EmptyState/EmptyState";
import { PageHeading } from "../Common/Typography/AppTypography";
import customConfig from "../../config";
import getFeedbackStatisticsUrl from "../../utils/getFeedbackStatisticsUrl";

const IFRAME_LOAD_TIMEOUT_MS = 10000;
const PAGE_MIN_HEIGHT = "calc(100vh - 180px)";

const LOAD_STATUS = {
  UNCONFIGURED: "unconfigured",
  LOADING: "loading",
  READY: "ready",
  ERROR: "error",
};

const FeedbackStatistics = ({ wrapperClass }) => {
  const statisticsUrl = useMemo(() => getFeedbackStatisticsUrl(), []);
  const loadTimeoutRef = useRef(null);

  const [loadStatus, setLoadStatus] = useState(() =>
    statisticsUrl ? LOAD_STATUS.LOADING : LOAD_STATUS.UNCONFIGURED
  );

  const clearLoadTimeout = () => {
    if (loadTimeoutRef.current) {
      clearTimeout(loadTimeoutRef.current);
      loadTimeoutRef.current = null;
    }
  };

  useEffect(() => {
    if (!statisticsUrl) {
      setLoadStatus(LOAD_STATUS.UNCONFIGURED);
      return undefined;
    }

    setLoadStatus(LOAD_STATUS.LOADING);
    clearLoadTimeout();
    loadTimeoutRef.current = setTimeout(() => {
      setLoadStatus(LOAD_STATUS.ERROR);
    }, IFRAME_LOAD_TIMEOUT_MS);

    return clearLoadTimeout;
  }, [statisticsUrl]);

  const handleIframeLoad = () => {
    clearLoadTimeout();
    setLoadStatus(LOAD_STATUS.READY);
  };

  const handleIframeError = () => {
    clearLoadTimeout();
    setLoadStatus(LOAD_STATUS.ERROR);
  };

  const isLoading = loadStatus === LOAD_STATUS.LOADING;
  const showIframe =
    Boolean(statisticsUrl) &&
    (loadStatus === LOAD_STATUS.LOADING || loadStatus === LOAD_STATUS.READY);

  return (
    <Fragment>
      <ResponsiveContentWrapper className={wrapperClass || ""}>
        <Box
          sx={{
            width: "100%",
            minWidth: 0,
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            height: "100%",
          }}
        >
          <PageHeading sx={{ mb: 2 }}>Feedback Statistics</PageHeading>

          {loadStatus === LOAD_STATUS.UNCONFIGURED && (
            <EmptyState
              message={customConfig.feedbackStatisticsNotConfiguredMsg}
              height="100%"
              minHeight={PAGE_MIN_HEIGHT}
            />
          )}

          {loadStatus === LOAD_STATUS.ERROR && (
            <EmptyState
              message={customConfig.feedbackStatisticsLoadErrorMsg}
              height="100%"
              minHeight={PAGE_MIN_HEIGHT}
            />
          )}

          {showIframe && (
            <Box
              sx={{
                position: "relative",
                flex: 1,
                width: "100%",
                minHeight: { xs: "60vh", sm: "65vh", md: PAGE_MIN_HEIGHT },
              }}
            >
              {isLoading && (
                <ScreenLoader
                  height="100%"
                  sx={{
                    position: "absolute",
                    inset: 0,
                    zIndex: 1,
                    minHeight: PAGE_MIN_HEIGHT,
                    backgroundColor: "background.default",
                  }}
                />
              )}
              <Box
                component="iframe"
                title="Feedback Statistics"
                src={statisticsUrl}
                onLoad={handleIframeLoad}
                onError={handleIframeError}
                sx={{
                  width: "100%",
                  height: "100%",
                  minHeight: { xs: "60vh", sm: "65vh", md: PAGE_MIN_HEIGHT },
                  border: "none",
                  display: "block",
                  visibility: isLoading ? "hidden" : "visible",
                }}
              />
            </Box>
          )}
        </Box>
      </ResponsiveContentWrapper>
    </Fragment>
  );
};

export default FeedbackStatistics;
